// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Transport session. Wraps trystero (nostr strategy) into typed actions for the
 * beam protocol. trystero discovers the peer over public nostr relays and
 * encrypts the signalling handshake with the room passphrase, so the relay only
 * sees ciphertext. Items themselves ride a direct WebRTC data channel (DTLS on
 * the wire) — no server is ever in the item path.
 *
 * Three actions:
 *   hello — announce this device's name (JSON)
 *   frame — one encrypted item frame: [iv | ciphertext] (binary; trystero chunks + progresses)
 *   ack   — a delivery receipt: { id } (JSON)
 */
import { joinRoom, selfId } from 'trystero/nostr';
import type { Room, BaseRoomConfig, JsonValue, MessageAction } from '@trystero-p2p/core';

const APP_ID = 'beamwell-v1-benrichardson-dev';

export interface Hello {
  name: string;
}

export interface SessionHandlers {
  onPeerJoin: (peerId: string) => void;
  onPeerLeave: (peerId: string) => void;
  onHello: (peerId: string, hello: Hello) => void;
  onFrame: (peerId: string, frame: Uint8Array) => void;
  onFrameProgress: (peerId: string, progress: number, id: string) => void;
  onAck: (peerId: string, id: string) => void;
  onError: (message: string) => void;
}

export class BeamSession {
  readonly selfId = selfId;
  private room: Room | null = null;
  private helloA: MessageAction<JsonValue> | null = null;
  private frameA: MessageAction<Uint8Array> | null = null;
  private ackA: MessageAction<JsonValue> | null = null;

  constructor(
    private roomId: string,
    private pass: string,
    private handlers: SessionHandlers,
  ) {}

  /** Join the room and wire the actions. Returns false if joining threw. */
  join(): boolean {
    const config: BaseRoomConfig = { appId: APP_ID };
    if (this.pass) config.password = this.pass;
    let room: Room;
    try {
      room = joinRoom(config, this.roomId);
    } catch (e) {
      this.handlers.onError(`Could not join: ${(e as Error).message}`);
      return false;
    }
    this.room = room;

    this.helloA = room.makeAction<JsonValue>('hello');
    this.frameA = room.makeAction<Uint8Array>('frame');
    this.ackA = room.makeAction<JsonValue>('ack');

    this.helloA.onMessage = (data, ctx) => this.handlers.onHello(ctx.peerId, data as unknown as Hello);
    this.frameA.onMessage = (data, ctx) => this.handlers.onFrame(ctx.peerId, data as Uint8Array);
    this.frameA.onReceiveProgress = (progress, ctx) =>
      this.handlers.onFrameProgress(ctx.peerId, progress, typeof ctx.metadata === 'string' ? ctx.metadata : '');
    this.ackA.onMessage = (data, ctx) => {
      const id = (data as unknown as { id?: string })?.id;
      if (typeof id === 'string') this.handlers.onAck(ctx.peerId, id);
    };

    room.onPeerJoin = (peerId) => this.handlers.onPeerJoin(peerId);
    room.onPeerLeave = (peerId) => this.handlers.onPeerLeave(peerId);
    return true;
  }

  sendHello(hello: Hello, target?: string): void {
    this.helloA?.send(hello as unknown as JsonValue, target ? { target } : undefined).catch(() => undefined);
  }

  sendAck(id: string, target: string): void {
    this.ackA?.send({ id } as unknown as JsonValue, { target }).catch(() => undefined);
  }

  /** Send one encrypted item frame, reporting 0..1 upload progress. The item id
   *  rides as trystero metadata so receive-progress can be attributed. */
  async sendFrame(target: string, frame: Uint8Array, id: string, onProgress?: (p: number) => void): Promise<void> {
    if (!this.frameA) throw new Error('not joined');
    await this.frameA.send(frame, {
      target,
      metadata: id,
      onProgress: onProgress ? (p) => onProgress(p) : undefined,
    });
  }

  getPeerIds(): string[] {
    return this.room ? Object.keys(this.room.getPeers()) : [];
  }

  leave(): void {
    if (!this.room) return;
    try {
      void this.room.leave();
    } catch {
      /* already left */
    }
    this.room = null;
  }
}
